import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { learningPathsAPI, enrollmentsAPI } from '../lib/api';
import {
  Route,
  BookOpen,
  Sparkles,
  ChevronRight,
  CheckCircle2,
  Circle,
  Award,
} from 'lucide-react';

export const LearningPaths = () => {
  const { t } = useTranslation();
  const [paths, setPaths] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pathsRes, enrollmentsRes] = await Promise.all([
          learningPathsAPI.getAll(),
          enrollmentsAPI.getAll(),
        ]);
        setPaths(pathsRes.data);
        setEnrollments(enrollmentsRes.data);
      } catch (error) {
        console.error('Failed to fetch learning paths:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getPathProgress = (path) => {
    const pathEnrollments = enrollments.filter(e => 
      path.course_ids.includes(e.course_id)
    );
    const completedCount = pathEnrollments.filter(e => e.status === 'completed').length;
    return {
      completed: completedCount,
      total: path.course_ids.length,
      percentage: path.course_ids.length > 0 
        ? (completedCount / path.course_ids.length) * 100 
        : 0,
      isComplete: completedCount === path.course_ids.length && path.course_ids.length > 0,
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-72" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="learning-paths-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Route className="w-8 h-8 text-primary" />
            {t('learningPaths.title', 'Learning Paths')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('learningPaths.subtitle', 'Follow structured learning journeys to master new skills')}
          </p>
        </div>

        {/* Paths Grid */}
        {paths.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {paths.map((path) => {
              const progress = getPathProgress(path);
              
              return (
                <Card 
                  key={path.id} 
                  className="group overflow-hidden card-hover"
                  data-testid={`path-card-${path.id}`}
                >
                  <div className="aspect-[2/1] relative overflow-hidden">
                    <img
                      src={path.thumbnail || 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=600&auto=format&fit=crop'}
                      alt={path.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute top-4 right-4 flex gap-2">
                      <Badge className="gold-gradient text-white border-0">
                        <Sparkles className="w-3 h-3 mr-1" />
                        +{path.points_bonus} {t('learningPaths.bonus', 'bonus')}
                      </Badge>
                    </div>
                    {progress.isComplete && (
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-green-500 text-white border-0">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {t('courses.completed', 'Completed')}
                        </Badge>
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1">{path.title}</h3>
                      <p className="text-white/80 text-sm line-clamp-2">{path.description}</p>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    {/* Progress */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">{t('courses.progress', 'Progress')}</span>
                        <span className="font-medium">
                          {progress.completed}/{progress.total} {t('learningPaths.courses', 'courses')}
                        </span>
                      </div>
                      <Progress value={progress.percentage} className="h-2" />
                    </div>

                    {/* Course List Preview */}
                    <div className="flex items-center gap-2 mb-4">
                      {path.course_ids.slice(0, 4).map((_, i) => {
                        const isComplete = i < progress.completed;
                        return (
                          <div
                            key={i}
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isComplete
                                ? 'bg-green-500/10 text-green-500'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {isComplete ? (
                              <CheckCircle2 className="w-4 h-4" />
                            ) : (
                              <Circle className="w-4 h-4" />
                            )}
                          </div>
                        );
                      })}
                      {path.course_ids.length > 4 && (
                        <span className="text-sm text-muted-foreground">
                          +{path.course_ids.length - 4} {t('common.more', 'more')}
                        </span>
                      )}
                    </div>

                    {/* Reward Badge */}
                    {path.badge_id && (
                      <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 mb-4">
                        <Award className="w-5 h-5 text-amber-500" />
                        <span className="text-sm font-medium">{t('learningPaths.earnBadge', 'Earn a special badge upon completion')}</span>
                      </div>
                    )}

                    {/* Action Button */}
                    <Button 
                      asChild 
                      className="w-full rounded-full"
                      data-testid={`view-path-${path.id}`}
                    >
                      <Link to={`/learning-paths/${path.id}`}>
                        {progress.completed > 0 ? t('learningPaths.continuePath', 'Continue Path') : t('learningPaths.startPath', 'Start Path')}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Route className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t('learningPaths.noPaths', 'No Learning Paths Yet')}</h3>
            <p className="text-muted-foreground mb-6">
              {t('learningPaths.noPathsDesc', 'Learning paths help you follow a structured curriculum.')}
            </p>
            <Button asChild>
              <Link to="/courses">{t('learningPaths.browseCourses', 'Browse Individual Courses')}</Link>
            </Button>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LearningPaths;
